import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import * as qs from 'qs';

import { getNeighborAisById } from '@helpers/getNeighborAisById';

import useTranslation from '@hooks/useTranslation';

import type { IActionItemGlobalItemOrId } from '@model/actionItem';

import { apiClient } from '@services/apiClient';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppPersistRefreshInterval } from '@store/useAppStorePersist';

export interface IUseActionItemsGlobalOrIdListKeys {
  filter: {
    urgency: string;
    type: string;
    state: string;
  };
  search: string;
  locationIds?: Array<number>;
  enabled?: boolean;
}

interface IResponse {
  critical: number;
  hasWorkOrder: boolean;
  high: number;
  needResponseList: IActionItemGlobalItemOrId[];
  standard: number;
  total_action_items: number;
  waitingList: IActionItemGlobalItemOrId[];
}

export const actionItemsGlobalOrIdListKey = (
  keys?: IUseActionItemsGlobalOrIdListKeys,
): [
  string,
  (
    | {
        urgency: string;
        type: string;
        state: string;
      }
    | undefined
  ),
  Array<number> | undefined,
  string,
] => ['actionItems', keys?.filter, keys?.locationIds, keys?.search || ''];

export const actionItemsGlobalOrIdListFn = ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof actionItemsGlobalOrIdListKey>>) => {
  const filters = {
    type: queryKey[1]?.type === 'all' ? undefined : queryKey[1]?.type,
    urgency: queryKey[1]?.urgency === 'all' ? undefined : queryKey[1]?.urgency,
    state: queryKey[1]?.state === 'all' ? undefined : queryKey[1]?.state,
  };

  const location_ids = queryKey[2];
  const trimmedSearchValue = queryKey[3]?.trim();
  const search = trimmedSearchValue === '' ? undefined : trimmedSearchValue;

  return apiClient.get(`${APP_API_URL_V2}customer-dashboard-v2/action-items/list`, {
    params: { filters, location_ids, search },
    paramsSerializer: {
      serialize: (params) => qs.stringify(params),
    },
  });
};

const actionItemsGlobalOrIdListSelect = (
  t: (text: string) => string,
  dateFormat: string,
  data: IResponse,
) => {
  const { needResponseList, waitingList, neighborAisById } = getNeighborAisById(
    t,
    dateFormat,
    data.needResponseList,
    data.waitingList,
  );

  return {
    data: {
      ...data,
      needResponseList,
      waitingList,
    },
    neighborAisById,
    totalCount: data.total_action_items,
  };
};
export const useActionItemsGlobalOrIdListQuery = (keys: IUseActionItemsGlobalOrIdListKeys) => {
  const { t } = useTranslation();

  const { data: profileData } = useUserProfileQuery();

  const refreshInterval = selectAppPersistRefreshInterval();

  const dateFormat = profileData?.dateFormat.date.slice(0, -2) || '';

  return useQuery({
    queryKey: actionItemsGlobalOrIdListKey(keys),
    queryFn: actionItemsGlobalOrIdListFn,
    refetchInterval: refreshInterval,
    enabled: !!keys.enabled,
    select: ({ data }: AxiosResponse<{ data: IResponse }>) =>
      actionItemsGlobalOrIdListSelect(t, dateFormat, data.data),
  });
};
